import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { PAGINATION_PAGE_SIZES } from '../../../utils/pagination'

import type { Props } from './Pagination.types'

function Pagination({ pagination, setPagination }: Props) {
  const { page = 0, pageSize = 10, count: totalCount = 0 } = pagination ?? {}
  const [gotoPage, setGotoPage] = useState('')
  const { t } = useTranslation()

  const totalPages = Math.ceil(totalCount / pageSize)
  const startIdx = totalCount === 0 ? 0 : page * pageSize + 1
  const endIdx = Math.min((page + 1) * pageSize, totalCount)

  function handlePageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = parseInt(e.target.value)

    if (value > 0) {
      setGotoPage(value.toString())
    } else {
      setGotoPage('')
    }
  }

  const handleGoto = () => {
    const num = parseInt(gotoPage, 10)

    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      setPagination({ ...pagination, page: num - 1 })
      setGotoPage('')
    }
  }

  return (
    <div className="flex justify-between w-full items-center mt-4 border-t border-gray-200 px-2 py-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            className="flex items-center gap-1 rounded-md border px-2 py-1 text-gray-600 disabled:opacity-50"
            disabled={page <= 0}
            onClick={() =>
              setPagination({ ...pagination, page: Math.max(page - 1, 0) })
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`rounded-md border px-2 py-1 ${
                page === i ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'
              }`}
              onClick={() => setPagination({ ...pagination, page: i })}
            >
              {i + 1}
            </button>
          ))}

          <button
            className="flex items-center gap-1 rounded-md border px-2 py-1 text-gray-600 disabled:opacity-50"
            disabled={page >= totalPages - 1}
            onClick={() =>
              setPagination({
                ...pagination,
                page: Math.min(page + 1, totalPages - 1),
              })
            }
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <input
              type="number"
              className="w-16 rounded-md border px-2 py-1 text-sm"
              placeholder="Page"
              value={gotoPage}
              min={1}
              onChange={handlePageChange}
            />
            <button
              className="rounded-lg border bg-blue-600 px-2 py-1 text-white hover:bg-blue-700 transition"
              disabled={!gotoPage}
              onClick={handleGoto}
            >
              {t('Pagination.Apply')}
            </button>
          </div>
        )}

        <span className="ml-4 text-gray-500">
          {startIdx} – {endIdx} {t('Pagination.RangeText')} {totalCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-gray-500">{t('Pagination.Dropdown')}</span>
        <select
          className="rounded-md border px-2 py-1 text-sm"
          value={pageSize}
          onChange={(e) =>
            setPagination({
              ...pagination,
              pageSize: Number(e.target.value),
              page: 0,
            })
          }
        >
          {PAGINATION_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export default memo(Pagination)
